// src/types/relationships.ts
// Typed entity-relationship substrate (the moat layer).
// Additive: lives ALONGSIDE the legacy untyped `associatedTags: string[]` on Tag.
// INVARIANTS: `type` enum is controlled (grows deliberately, never silently); free-text
// only in `role` + `description`; no `wallet` identity kind.

/**
 * Curated starter vocabulary (Lam, 2026-06-07). Controlled + machine-queryable.
 * Covers the founder narrative AND the meme/mascot narrative day one.
 * Grows deliberately — adding a type is a code change + review, never free-text.
 * PARTNER_OF and ASSOCIATED_WITH are symmetric; the rest are directed with derived inverse labels.
 */
export type RelationshipType =
  | 'FOUNDED'        // person → org/project
  | 'WORKED_AT'      // person → org (dated)
  | 'SUBSIDIARY_OF'  // org/project → parent org (inverse: 'operates') — corporate/product spine
  | 'MASCOT_OF'      // character/figure → brand (e.g. Bandit-the-dog → brand)
  | 'INSPIRED_BY'    // project → its source/inspiration (e.g. meme coin → the thing)
  | 'PARTNER_OF'     // org ↔ org (SYMMETRIC)
  | 'BUILT_ON'       // project → protocol (NOT chains — chain membership is the chains[] facet)
  | 'DEVELOPED_BY'   // product/game → studio/org (inverse: 'developer of') — creator relation, not corporate ownership
  | 'ASSOCIATED_WITH'; // any ↔ any (SYMMETRIC) — general lore link (couples, cross-brand characters).
                       // `description` is MANDATORY (enforced in validateEdgeInput): the per-edge
                       // narrative IS the user-facing explanation — no description, no edge.

/** Node types (informational; entityType on the Tag remains the source of truth). */
export type EntityNodeType = 'PERSON' | 'ORG' | 'PROJECT';

/** Single user-facing confidence label. Optional human override; DEFAULT computed from sources[]. */
export type Confidence = 'high' | 'medium' | 'low';

/** Review-queue lifecycle. Read path shows 'confirmed' by default. 'rejected' kept as negative knowledge. */
export type ReviewStatus = 'proposed' | 'confirmed' | 'rejected';

/** Temporal tenure — DERIVED from endDate, never stored (avoids drift). Distinct from ReviewStatus. */
export type Tenure = 'current' | 'former';

/** How a single source established the claim. Drives the per-source strength in confidence scoring. */
export type VerificationMethod =
  | 'signed-message'
  | 'onchain'
  | 'human-confirmed'
  | 'official-bio'
  | 'news'
  | 'agent-inferred';

/**
 * One piece of evidence. An edge carries an ARRAY of these; confidence rises with independent
 * corroboration. `snippet` (the quoted supporting text) makes the claim auditable / re-checkable.
 * `independent: false` marks self-sourced evidence (e.g. our own recap) → EXCLUDED from corroboration
 * (the anti-circular-citation firewall).
 */
export interface RelationshipSource {
  sourceType: 'official-bio' | 'onchain' | 'news' | 'human-confirmed' | 'agent-inferred' | 'self';
  url?: string;
  snippet?: string;              // the quoted evidence text
  observedAt: string;            // ISO 8601
  asserter: string;              // 'human:<uid>' | 'model:<model-id>'
  verificationMethod: VerificationMethod;
  independent?: boolean;         // default true; false = self-sourced (firewall: not counted for corroboration)
}

/** One directed, temporal edge. Multiple intervals = multiple docs. */
export interface EntityRelationship {
  id: string;
  fromId: string;                // ⭐ IMMUTABLE join key — the Tag doc id (e.g. 'a1b2c3' or 'pudgy-penguins')
  toId: string;                  // ⭐ IMMUTABLE join key — the target Tag doc id
  fromSlug: string;              // denormalized for display, refreshed on rename — e.g. 'luca-netz'
  toSlug: string;                // denormalized for display — e.g. 'pudgy-penguins'
  fromName: string;              // denormalized display name — refreshed on rename (kills the frontend join)
  toName: string;                // denormalized display name
  fromType?: string | null;      // denormalized source entityType (for 'in'-direction node-card rendering)
  toType?: string | null;        // denormalized target entityType (for node-card rendering without a join)
  type: RelationshipType;
  role?: string;                 // free-text refinement ONLY: 'CEO', 'art lead'
  description?: string;          // free-text NARRATIVE — the "why", only when structure can't carry it
                                 //   ("a meme coin named after Luca's real-life pet dog").
                                 //   Absent → renderers fall back to a templated line (see narrativeLine).
  startDate?: string | null;     // ISO date or null
  endDate?: string | null;       // null => current (Tenure is DERIVED, never stored)
  status: ReviewStatus;          // review lifecycle; read path shows 'confirmed' by default
  sources: RelationshipSource[]; // corroboration array — confidence computed from it
  confidence?: Confidence;       // optional HUMAN override; if absent, computed via confidenceFromSources
  lastVerifiedAt?: string | null;
  sponsored?: boolean;           // trust firewall: subject-funded → hard-excluded from independent content
  createdAt: string;
  updatedAt: string;
}

/** Identity claim — separate collection, many-to-many, temporal. NO wallet kind (legal guardrail). */
// 'domain' (WS-K L3): a DNS-/well-known-verified web domain owned by the entity. Distinct from a
// website LINK (which is just a pointer) — a 'domain' claim is PROVEN ownership of the domain.
export type IdentityClaimKind = 'x' | 'ens' | 'github' | 'deployer' | 'domain';
export interface IdentityClaim {
  id: string;
  kind: IdentityClaimKind;
  value: string;
  entitySlug: string;
  verificationMethod: VerificationMethod;
  observedAt: string;
  source: string;
}

/** Derived tenure — NEVER stored (avoids drift). */
export function deriveTenure(edge: Pick<EntityRelationship, 'endDate'>): Tenure {
  return edge.endDate == null ? 'current' : 'former';
}

/** Dedup/upsert key on IMMUTABLE ids; MUST include startDate, else history collapses. */
export function relationshipKey(
  e: Pick<EntityRelationship, 'fromId' | 'toId' | 'type' | 'startDate'>
): string {
  return [e.fromId, e.toId, e.type, e.startDate ?? 'null'].join('__');
}

/**
 * Confidence scoring is single-sourced in `shared/graph/confidence.js` (consolidation U11)
 * and consumed by BOTH runtimes — this ESM/TS frontend and the Node/ESM MCP backend
 * (`backend/src/mcp/lib/relationship-graph.mjs`). Re-exported here so the many callers that
 * `import { confidenceFromSources, effectiveConfidence } from '@/types/relationships'` keep
 * their import path. The anti-circular-citation firewall lives in that shared module.
 */
export { confidenceFromSources, effectiveConfidence } from '@shared/graph/confidence';
